import { Alert, Button, Spin } from "antd";
import { observer } from "mobx-react-lite";
import * as React from "react";
import { HttpErrorKind, httpErrorKind } from "../model/httpError";
import { ResourceState, matchResource } from "../model/resourceState";

/**
 * Что показать вместо содержимого, пока данные грузятся или не пришли.
 *
 * Текст ошибки — таблица по варианту: пользователю нужна причина, а не статус.
 * Технические подробности сюда не попадают.
 */
const errorMessages: Record<HttpErrorKind, string> = {
  forbidden: "Нет доступа к объекту. Возможно, изменились права.",
  notFound: "Объект был удалён.",
  other: "Не удалось загрузить объект.",
};

const waitBox: React.CSSProperties = {
  flex: 1,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
};

interface PropsResourceView<T> {
  state: ResourceState<T>;
  onRetry(): void;
  /** Чем объяснить отказ. По умолчанию — причинами загрузки объекта. */
  errorMessage?(error: unknown): string;
  children(value: T): React.ReactNode;
}

/**
 * Локальное состояние одной величины: загрузка, ошибка с повтором или данные.
 * Ошибка одного объекта остаётся его ошибкой и не переводит вкладку целиком
 * в состояние отказа.
 */
export const objectErrorMessage = (error: unknown): string =>
  errorMessages[httpErrorKind(error)];

const resourceView = <T,>({
  state,
  onRetry,
  errorMessage = objectErrorMessage,
  children,
}: PropsResourceView<T>): React.ReactElement | null =>
  matchResource<T, React.ReactElement | null>(state, {
    none: () => null,
    wait: () => (
      <div style={waitBox}>
        <Spin size="large" />
      </div>
    ),
    ready: (value) => <>{children(value)}</>,
    error: (error) => (
      <Alert
        type="error"
        showIcon
        message={errorMessage(error)}
        action={
          <Button size="small" onClick={onRetry}>
            Повторить
          </Button>
        }
      />
    ),
  });

/**
 * Наблюдатель обязателен.
 *
 * Содержимое приходит функцией и вызывается здесь, поэтому всё, что оно читает
 * из сторов, читается в момент отрисовки этого компонента. Без `observer` такие
 * чтения никем не отслеживаются, и вкладка не узнаёт об изменениях - именно так
 * переставало открываться окно подтверждения удаления.
 *
 * Приведение возвращает дженерик: `observer` его теряет.
 */
export const ResourceView = observer(resourceView) as typeof resourceView;
